import { useCallback, useEffect, useState } from 'react'
import { ListFilterDrawer } from '@/components/ui/ListFilterDrawer'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { useAppSelector } from '@/app/hooks'
import { selectIsAuthenticated, selectUser } from '@/features/auth/authSlice'
import { getCompanies } from '@/features/invitee/services/inviteeService'
import { useUserRole } from '@/hooks/useUserRole'
import { formatDateForAPI } from '@/features/invitee/utils/inviteeFormatters'
import { ReportDateRangePicker } from './ReportDateRangePicker'
import type { PmcPerformanceRedFilterState } from '../types/filters'
import type { PmcPerformanceRedFiltersProps } from '../types/components'



import { LABEL_CLASS } from '../constants/configuredReportStyles'

export function PmcPerformanceRedFilters({
  filters,
  salesTeams,
  isOpen,
  onClose,
  onApply,
  onReset,
  onRollingToggle,
}: PmcPerformanceRedFiltersProps) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const user = useAppSelector(selectUser)
  const { companyId } = useUserRole()
  const lockedCompanyId = companyId != null ? String(companyId) : undefined

  const [draft, setDraft] = useState<PmcPerformanceRedFilterState>(filters)
  const [companies, setCompanies] = useState<SearchableSelectOption[]>([])

  const teamOptions: SearchableSelectOption[] = salesTeams.map((t) => ({
    value: String(t.sales_team_id),
    label: t.team_name,
  }))

  const loadCompanies = useCallback(async (search: string) => {
    if (search.length % 2 !== 0 && search.length > 0) return
    const list = await getCompanies(search)
    setCompanies(
      list.map((c) => ({
        value: String(c.company_id),
        label: c.company_name ?? String(c.company_id),
      })),
    )
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: sync draft to incoming filters when the drawer opens
    if (isOpen) setDraft(filters)
  }, [isOpen, filters])

  useEffect(() => {
    if (!isAuthenticated || !isOpen) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: data-load-on-open populates the company dropdown (setState is async, after await)
    void loadCompanies('')
  }, [isAuthenticated, isOpen, loadCompanies])

  const companyOptions = lockedCompanyId
    ? [{ value: lockedCompanyId, label: user?.current_role?.company_name || lockedCompanyId }]
    : companies

  const patch = (partial: Partial<PmcPerformanceRedFilterState>) => {
    setDraft((prev) => ({ ...prev, ...partial }))
  }

  return (
    <ListFilterDrawer
      isOpen={isOpen}
      onClose={onClose}
      onReset={onReset}
      onDone={() => {
        onApply(draft)
        onClose()
      }}
      testIdPrefix="pmc-performance-red"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label className={LABEL_CLASS}>Sales team</label>
          <SearchableSelect
            options={teamOptions}
            value={draft.sales_team}
            onChange={(val) => patch({ sales_team: Array.isArray(val) ? val : [] })}
            placeholder="All Teams"
            multiple
          />
        </div>

        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={draft.isRolling}
              onChange={(e) => setDraft(onRollingToggle(e.target.checked))}
              className="h-4 w-4 rounded border-slate-300"
            />
            Rolling 52 weeks
          </label>
          <ReportDateRangePicker
            startDate={draft.fromDt}
            endDate={draft.toDt}
            onChange={(start, end) => {
              if (draft.isRolling && start) {
                const rollingEnd = new Date(start)
                rollingEnd.setFullYear(rollingEnd.getFullYear() + 1)
                patch({
                  fromDt: formatDateForAPI(start),
                  toDt: formatDateForAPI(rollingEnd),
                })
                return
              }
              patch({
                fromDt: start ? formatDateForAPI(start) : '',
                toDt: end ? formatDateForAPI(end) : '',
              })
            }}
            dropdownPanelClassName="[&>div:first-child>div]:!grid-cols-3 [&>div:first-child_button]:!min-w-0 [&>div:first-child_button]:!whitespace-normal [&>div:first-child_button]:leading-snug"
          />
        </div>

        <div className="space-y-2">
          <label className={LABEL_CLASS}>Company</label>
          <SearchableSelect
            options={companyOptions}
            value={lockedCompanyId || draft.company_id || ''}
            onChange={(val) => {
              const company_id = Array.isArray(val) ? (val[0] ?? '') : val
              const company_name =
                companyOptions.find((o) => o.value === company_id)?.label ?? ''
              patch({ company_id, company_name })
            }}
            onSearch={lockedCompanyId ? undefined : loadCompanies}
            placeholder="All Company"
            disabled={Boolean(lockedCompanyId)}
          />
        </div>
      </div>
    </ListFilterDrawer>
  )
}

export type { PmcPerformanceRedFilterState } from '../types/filters'

